import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Package, RefreshCw, X, ClipboardList, Truck, Trash2, Filter, Search, ChevronLeft, ChevronRight, FileSpreadsheet, Download, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import AddressSearch from '@/components/AddressSearch';
import BulkInvoiceUploadModal from './BulkInvoiceUploadModal';
import SellerCombinedShipmentModal from './SellerCombinedShipmentModal';
import { downloadOrders } from './exportOrders';
// STEP 3-OPS-C · REUSE existing trackingUrl helper (LOCK · no external API · official carrier URLs only).
// STEP 3-OPS-C-HOTFIX · use internal-only label (Carrier 실시간 상태 흉내 금지).
import { getTrackingAvailable, getTrackingUrl, getInternalShipmentLabel } from '@/lib/trackingUrl';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const STATUS_TABS = [
  { key: '',                 label: '전체' },
  { key: 'payment_pending',  label: '결제대기' },
  { key: 'paid',             label: '결제완료' },
  { key: 'preparing',        label: '배송준비' },
  { key: 'shipping',         label: '배송중' },
  { key: 'delivered',        label: '배송완료' },
  { key: 'cancelled',        label: '취소/환불' },
];

const STATUS_STYLE = {
  payment_pending: 'text-amber-700 bg-amber-100 dark:bg-amber-500/20',
  paid:            'text-blue-700 bg-blue-100 dark:bg-blue-500/20',
  preparing:       'text-indigo-700 bg-indigo-100 dark:bg-indigo-500/20',
  shipping:        'text-violet-700 bg-violet-100 dark:bg-violet-500/20',
  delivered:       'text-emerald-700 bg-emerald-100 dark:bg-emerald-500/20',
  confirmed:       'text-emerald-700 bg-emerald-100 dark:bg-emerald-500/20',
  cancelled:       'text-gray-600 bg-gray-100 dark:bg-white/10',
  refund_requested:'text-orange-700 bg-orange-100 dark:bg-orange-500/20',
  refunded:        'text-gray-600 bg-gray-100 dark:bg-white/10',
};

const ROLLBACK_MAP = {
  'paid':       { prev: 'payment_pending', label: '결제대기로 되돌리기' },
  'preparing':  { prev: 'paid',            label: '결제완료로 되돌리기' },
  'shipping':   { prev: 'preparing',       label: '배송준비로 되돌리기' },
  'delivered':  { prev: 'shipping',        label: '배송중으로 되돌리기' },
};

const ROLLBACK_REASONS = ['입금 미확인', '잘못 처리함', '실제 발송 전', '송장정보 오류', '택배사 집하 취소', '기타'];

const RollbackConfirmModal = ({ open, from, count = 1, busy, onCancel, onConfirm }) => {
  const [reasonPick, setReasonPick] = useState('입금 미확인');
  const [reasonText, setReasonText] = useState('');
  const [keepShipment, setKeepShipment] = useState(true);
  useEffect(() => { if (open) { setReasonPick('입금 미확인'); setReasonText(''); setKeepShipment(true); } }, [open]);
  if (!open) return null;
  const info = ROLLBACK_MAP[from];
  if (!info) return null;
  const finalReason = reasonPick === '기타' ? reasonText.trim() : reasonPick;
  const disabled = busy || !finalReason;
  const showKeepChoice = from === 'shipping';
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-6" onClick={onCancel} data-testid="rollback-modal">
      <div className="bg-white dark:bg-[#141414] w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-black mb-2">이전 단계로 되돌리기</h3>
        <p className="text-xs text-gray-500 mb-3">
          {count > 1 ? `선택한 ${count}건을 ` : ''}
          <span className="font-bold">{from}</span> → <span className="font-bold text-[#FF3B30]">{info.prev}</span> 로 되돌리시겠습니까?
        </p>
        <label className="block text-xs font-bold mb-1">되돌리기 사유</label>
        <select value={reasonPick} onChange={(e) => setReasonPick(e.target.value)}
          className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent mb-2"
          data-testid="rollback-reason-select">
          {ROLLBACK_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {reasonPick === '기타' && (
          <input value={reasonText} onChange={(e) => setReasonText(e.target.value)}
            placeholder="사유를 직접 입력해주세요"
            className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent mb-2"
            data-testid="rollback-reason-text" />
        )}
        {showKeepChoice && (
          <div className="mb-3 space-y-1">
            <p className="text-xs font-bold">송장 정보</p>
            <label className="flex items-center gap-2 text-xs">
              <input type="radio" checked={keepShipment} onChange={() => setKeepShipment(true)} data-testid="rollback-keep-ship" />
              송장정보 유지 (기본)
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="radio" checked={!keepShipment} onChange={() => setKeepShipment(false)} data-testid="rollback-drop-ship" />
              송장정보 삭제
            </label>
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <button type="button" disabled={disabled}
            onClick={() => onConfirm({ reason: finalReason, keep_shipment: keepShipment })}
            className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40"
            data-testid="rollback-confirm">{busy ? '처리 중…' : '되돌리기'}</button>
          <button type="button" onClick={onCancel}
            className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold"
            data-testid="rollback-cancel">취소</button>
        </div>
      </div>
    </div>
  );
};


const COURIERS = ['CJ대한통운', '한진택배', '롯데택배', '우체국택배', '로젠택배', '경동택배', 'GS Postbox', '기타'];

const ACTION_LABELS = {
  'status.update': '상태 변경',
  'status.rollback':'이전 단계로 되돌리기',
  'invoice.set':   '송장 등록',
  'address.update':'배송정보 수정',
  'cancel':        '취소',
  'refund':        '환불',
  'delete':        '주문 삭제',
};

// Statuses at which a seller may still soft-delete their own order.
const DELETABLE_STATUSES = new Set(['payment_pending', 'paid']);

// --- Shared shipping-address edit form (used by seller detail modal) ---
const ShippingAddressEditor = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({ ...initial });
  const detailRef = useRef(null);
  const apply = (r) => setForm((f) => ({
    ...f,
    postal_code: r.postal_code,
    road_address: r.road_address,
    jibun_address: r.jibun_address,
    base_address: r.base_address,
    extra_address: r.extra_address,
    address1: (r.base_address + (r.extra_address ? ' ' + r.extra_address : '')).trim(),
  }));
  const cls = "px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="seller-shipping-edit">
      <input placeholder="받는 사람" className={cls} value={form.recipient_name || ''} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="ship-edit-name" />
      <input placeholder="휴대전화" className={cls} value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ship-edit-phone" />
      <div className="flex gap-2">
        <input placeholder="우편번호" className={`${cls} flex-1`} readOnly value={form.postal_code || ''} data-testid="ship-edit-postal" />
        <AddressSearch onComplete={apply} detailInputRef={detailRef} testId="ship-edit-search" />
      </div>
      <input placeholder="기본 주소" className={`${cls} sm:col-span-1`} readOnly value={form.address1 || ''} data-testid="ship-edit-line1" />
      <input ref={detailRef} placeholder="상세 주소" className={`${cls} sm:col-span-2`}
        value={form.address2 || ''}
        onChange={(e) => setForm({ ...form, address2: e.target.value, detail_address: e.target.value })}
        data-testid="ship-edit-line2" />
      <input placeholder="배송 요청사항" className={`${cls} sm:col-span-2`}
        value={form.buyer_message || ''}
        onChange={(e) => setForm({ ...form, buyer_message: e.target.value })}
        data-testid="ship-edit-message" />
      <input placeholder="변경 사유(선택)" className={`${cls} sm:col-span-2`}
        value={form.reason || ''}
        onChange={(e) => setForm({ ...form, reason: e.target.value })}
        data-testid="ship-edit-reason" />
      <div className="flex gap-2 sm:col-span-2">
        <button type="button" disabled={saving} onClick={() => onSave(form)}
          className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40"
          data-testid="ship-edit-save">{saving ? '저장 중…' : '저장'}</button>
        <button type="button" onClick={onCancel}
          className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold"
          data-testid="ship-edit-cancel">취소</button>
      </div>
    </div>
  );
};

// --- Detail modal for a single seller_order ---
export const SellerOrderDetail = ({ id, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [invMode, setInvMode] = useState(false);
  const [inv, setInv] = useState({ courier: 'CJ대한통운', tracking_number: '' });
  const [rollbackOpen, setRollbackOpen] = useState(false);

  const load = async () => {
    try {
      const [d, h] = await Promise.all([
        api.get(`/seller/orders/${id}`),
        api.get(`/seller/orders/${id}/history`).catch(() => ({ data: [] })),
      ]);
      setDoc(d.data);
      setHistory(h.data || []);
      setInv({
        courier: d.data?.shipment?.courier || 'CJ대한통운',
        tracking_number: d.data?.shipment?.tracking_number || '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '주문을 불러오지 못했습니다.');
      onClose();
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const changeStatus = async (target) => {
    if (!window.confirm(`상태를 "${target}"로 변경하시겠습니까?`)) return;
    setBusy(true);
    try {
      await api.patch(`/seller/orders/${id}/status`, { status: target });
      toast.success('상태가 변경되었습니다.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '변경 실패'); }
    finally { setBusy(false); }
  };
  const saveInvoice = async () => {
    if (!inv.courier || !inv.tracking_number.trim()) { toast.error('택배사와 송장번호를 입력하세요.'); return; }
    setBusy(true);
    try {
      await api.patch(`/seller/orders/${id}/invoice`, { courier: inv.courier, tracking_number: inv.tracking_number.trim() });
      toast.success('송장이 등록되어 배송중으로 변경되었습니다.');
      setInvMode(false); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '송장 저장 실패'); }
    finally { setBusy(false); }
  };
  const saveShipping = async (form) => {
    setBusy(true);
    try {
      await api.patch(`/seller/orders/${id}/shipping-address`, {
        ...form,
        expected_updated_at: doc?.updated_at,
      });
      toast.success('배송정보가 수정되었습니다.');
      setEditing(false); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '수정 실패'); }
    finally { setBusy(false); }
  };
  const createVirtualInvoice = async () => {
    // Phase 11-T: server-side backend gates by DELIVERY_MODE=test. Frontend
    // shows the button unconditionally in the invoice input mode; if prod
    // mode is active later, the backend returns 400 and we surface the toast.
    setBusy(true);
    try {
      const r = await api.post(`/seller/orders/${doc.order_id}/test-delivery`, { action: 'create_invoice', carrier: 'cj' });
      const t = r?.data?.order?.tracking_number || r?.data?.order?.shipment?.tracking_number || '';
      setInv({ courier: 'CJ대한통운', tracking_number: t });
      toast.success(`가상 송장 생성됨: ${t}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '가상 송장 생성 실패'); }
    finally { setBusy(false); }
  };
  const doRollback = async ({ reason, keep_shipment }) => {
    setBusy(true);
    try {
      await api.post(`/seller/orders/${id}/rollback`, { reason, keep_shipment });
      toast.success('이전 단계로 되돌렸습니다.');
      setRollbackOpen(false); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '되돌리기 실패'); }
    finally { setBusy(false); }
  };
  // LIGHTPAY-PG-FINAL-FIX-1 · 판매자 카드결제 취소 (기존 라이트페이 취소 API · 같은 주문은 같은 request_id 로 중복 차단).
  const doCardCancel = async () => {
    if (!window.confirm('카드결제를 전액 취소할까요? 라이트페이에 실제 취소가 요청되며 되돌릴 수 없습니다.')) return;
    setBusy(true);
    try {
      await api.post('/payments/lightpay/cancel', { seller_order_id: id, request_id: `seller-cancel:${id}`, reason: '판매자 결제취소' });
      toast.success('카드결제가 취소되었습니다.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '결제취소 실패'); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    if (!window.confirm('정말 삭제하시겠습니까? 삭제된 주문은 목록에서 보이지 않게 됩니다.')) return;
    setBusy(true);
    try {
      await api.delete(`/seller/orders/${id}`);
      toast.success('주문이 삭제되었습니다.');
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '삭제 실패'); }
    finally { setBusy(false); }
  };

  if (!doc) return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="seller-detail-modal">
      <div className="bg-white dark:bg-[#141414] rounded-2xl p-6 text-sm text-gray-400">불러오는 중…</div>
    </div>
  );

  const addr = doc.shipping_address_snapshot || {};
  const ship = doc.shipment || {};
  const isPreShip = ['paid', 'preparing'].includes(doc.status);
  const canEditAddress = ['payment_pending', 'paid', 'preparing'].includes(doc.status);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-6" onClick={onClose} data-testid="seller-detail-modal">
      <div className="bg-white dark:bg-[#141414] w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-black">주문 상세 · {doc.order_number}</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              주문일시: {doc.created_at && format(new Date(doc.created_at), 'yyyy.MM.dd HH:mm', { locale: ko })}
              <span className="mx-1.5">·</span>
              결제: {doc.payment_status || 'pending'}
            </p>
          </div>
          <button onClick={onClose} className="p-1" aria-label="닫기" data-testid="seller-detail-close"><X size={16} /></button>
        </div>

        <div className="mb-3 flex items-center gap-2">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLE[doc.status] || 'bg-gray-100'}`} data-testid="seller-detail-status">{doc.status_label}</span>
          <span className="text-[11px] text-gray-500">seller_order_id: {doc.seller_order_id}</span>
          {doc.payment_provider === 'lightpay' && isPreShip && doc.payment_status === 'paid' && (
            <button type="button" onClick={doCardCancel} disabled={busy}
                    className="ml-auto px-3 py-1 rounded-full border border-[#FF3B30] text-[#FF3B30] text-[11px] font-black hover:bg-[#FF3B30]/10 disabled:opacity-40"
                    data-testid="seller-detail-card-cancel">카드결제 취소</button>
          )}
          {doc.payment_status === 'refunded' && (
            <span className="ml-auto text-[11px] font-black text-[#FF3B30]" data-testid="seller-detail-refunded">전액 환불 · 출고 불가</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-black/10 dark:border-white/10 p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-black">구매자 · 배송지</p>
              {canEditAddress && !editing && (
                <button onClick={() => setEditing(true)} className="text-[11px] font-bold text-[#FF3B30]" data-testid="seller-detail-edit-shipping">배송정보 수정</button>
              )}
            </div>
            {editing ? (
              <ShippingAddressEditor
                initial={{ ...addr, buyer_message: doc.buyer_message || '' }}
                onSave={saveShipping} onCancel={() => setEditing(false)} saving={busy}
              />
            ) : (
              <div className="text-xs space-y-0.5">
                <p><span className="text-gray-500">구매자:</span> {doc.buyer_snapshot?.name || '-'} · {doc.buyer_snapshot?.email || ''}</p>
                <p><span className="text-gray-500">받는 사람:</span> {addr.recipient_name} · {addr.phone}</p>
                <p><span className="text-gray-500">주소:</span> [{addr.postal_code}] {addr.address1} {addr.address2}</p>
                {doc.buyer_message && <p><span className="text-gray-500">요청사항:</span> {doc.buyer_message}</p>}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-black/10 dark:border-white/10 p-3">
            <p className="text-xs font-black mb-1">택배 · 송장</p>
            {invMode ? (
              <div className="space-y-2">
                <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-2">
                  <p className="text-[10px] font-black text-orange-700 dark:text-orange-400 mb-1">🧪 개발자 테스트 · Mock 배송 어댑터</p>
                  <button onClick={createVirtualInvoice} disabled={busy}
                    className="w-full px-3 py-1.5 rounded-full text-[11px] font-black bg-black text-white disabled:opacity-40"
                    data-testid="seller-detail-virtual-invoice">
                    🧪 가상 송장 생성 (TEST-CJ-XXXXXXXX)
                  </button>
                  <p className="text-[10px] text-gray-500 mt-1">실제 CJ대한통운 API 호출 없음 · 아래 입력창에 자동 입력</p>
                </div>
                <select value={inv.courier} onChange={(e) => setInv({ ...inv, courier: e.target.value })}
                  className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent"
                  data-testid="seller-detail-invoice-courier">
                  {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={inv.tracking_number} onChange={(e) => setInv({ ...inv, tracking_number: e.target.value })}
                  placeholder="송장번호" className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent"
                  data-testid="seller-detail-invoice-tracking" />
                <div className="flex gap-2">
                  <button onClick={saveInvoice} disabled={busy || !isPreShip && doc.status !== 'shipping'} className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid="seller-detail-invoice-save">{doc.status === 'shipping' ? '수정 저장' : '저장 · 배송중 처리'}</button>
                  <button onClick={() => setInvMode(false)} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold">취소</button>
                </div>
              </div>
            ) : (
              <div className="text-xs space-y-0.5">
                <p><span className="text-gray-500">택배사:</span> {ship.courier || '-'}</p>
                <p><span className="text-gray-500">송장번호:</span> <span className="font-mono">{ship.tracking_number || '-'}</span></p>
                {ship.shipped_at && <p><span className="text-gray-500">발송일:</span> {format(new Date(ship.shipped_at), 'yyyy.MM.dd HH:mm', { locale: ko })}</p>}
                {ship.delivered_at && <p className="text-emerald-600">배송완료: {format(new Date(ship.delivered_at), 'yyyy.MM.dd HH:mm', { locale: ko })}</p>}
                {(isPreShip || doc.status === 'shipping') && (
                  <button onClick={() => setInvMode(true)} className="mt-2 text-[11px] font-bold text-[#FF3B30]" data-testid="seller-detail-invoice-toggle">
                    {doc.status === 'shipping' ? '송장 수정' : '송장 입력 · 배송 시작'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-4">
          <p className="text-xs font-black mb-2">상품 ({(doc.items || []).length}건)</p>
          <ul className="space-y-2">
            {(doc.items || []).map((it, idx) => (
              <li key={idx} className="flex items-center gap-2">
                <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
                  <SafeImg src={it.image_snapshot} kind="product" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{it.name_snapshot}</p>
                  {Object.keys(it.option || {}).length > 0 && (
                    <p className="text-[10px] text-gray-500 truncate">{Object.entries(it.option).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
                  )}
                  <p className="text-[10px] text-gray-500">{won(it.unit_price)} × {it.quantity}</p>
                </div>
                <span className="text-xs font-bold">{won(it.line_total)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-2 border-t border-black/5 dark:border-white/10 flex items-center justify-between text-xs">
            <span className="text-gray-500">상품 {won(doc.totals?.subtotal)} + 배송비 {won(doc.totals?.shipping_fee)}</span>
            <span className="font-black">{won(doc.totals?.total)}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {doc.status === 'payment_pending' && (
            <button onClick={() => changeStatus('paid')} disabled={busy}
              className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white"
              data-testid="seller-detail-status-paid">결제완료 처리</button>
          )}
          {/* STEP 2-P7-L · 3PL 물류센터 배정 (REUSE existing assign API · no new backend) */}
          {['paid', 'preparing'].includes(doc.status) && !doc.fulfillment_assignment_id && (
            <button onClick={async () => {
              const opId = window.prompt('3PL Operator ID\n예: op_hotfix_demo', 'op_hotfix_demo');
              if (!opId) return;
              const whId = window.prompt('Warehouse ID\n예: wh_hotfix_demo', 'wh_hotfix_demo');
              if (!whId) return;
              setBusy(true);
              try {
                await api.post(`/seller/seller-orders/${doc.seller_order_id}/assign`,
                                { operator_id: opId.trim(), warehouse_id: whId.trim() });
                toast.success('3PL 물류센터에 배정되었습니다.');
                // eslint-disable-next-line no-undef
                onSaved?.();
              } catch (e) {
                toast.error(e?.response?.data?.detail || '3PL 배정 실패');
              } finally { setBusy(false); }
            }} disabled={busy} className="px-3 py-1.5 rounded-full text-xs font-bold border border-black/20 dark:border-white/20"
              data-testid="seller-detail-assign-3pl">3PL 물류센터 배정</button>
          )}
          {doc.fulfillment_assignment_id && (
            <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"
              data-testid="seller-detail-assignment-info">
              3PL 배정됨 · {doc.fulfillment_assignment_id}
            </span>
          )}
          {doc.status === 'paid' && (
            <button onClick={() => changeStatus('preparing')} disabled={busy} className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white" data-testid="seller-detail-status-preparing">배송준비 처리</button>
          )}
          {doc.status === 'preparing' && (
            <>
              <button onClick={() => setInvMode(true)} disabled={busy} className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white" data-testid="seller-detail-invoice-start">송장입력</button>
              <button onClick={() => changeStatus('shipping')} disabled={busy || !doc.shipment?.tracking_number}
                title={!doc.shipment?.tracking_number ? '송장 입력 후 사용하세요.' : ''}
                className="px-3 py-1.5 rounded-full text-xs font-bold border border-violet-500/40 text-violet-600 disabled:opacity-40"
                data-testid="seller-detail-status-shipping">배송중 처리</button>
            </>
          )}
          {doc.status === 'shipping' && (
            <button onClick={() => changeStatus('delivered')} disabled={busy} className="px-3 py-1.5 rounded-full text-xs font-bold border border-emerald-500/40 text-emerald-600" data-testid="seller-detail-status-delivered">배송완료 처리</button>
          )}
          {ROLLBACK_MAP[doc.status] && (
            <button onClick={() => setRollbackOpen(true)} disabled={busy}
              className="px-3 py-1.5 rounded-full text-xs font-bold border border-amber-500/40 text-amber-700 dark:text-amber-400 inline-flex items-center gap-1"
              data-testid="seller-detail-rollback">↶ {ROLLBACK_MAP[doc.status].label}</button>
          )}
          {DELETABLE_STATUSES.has(doc.status) && (
            <button onClick={doDelete} disabled={busy}
              className="px-3 py-1.5 rounded-full text-xs font-bold border border-red-500/40 text-red-600 inline-flex items-center gap-1 ml-auto"
              data-testid="seller-detail-delete"><Trash2 size={12} /> 삭제</button>
          )}
        </div>

        <RollbackConfirmModal open={rollbackOpen} from={doc.status} busy={busy}
          onCancel={() => setRollbackOpen(false)} onConfirm={doRollback} />

        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3">
          <p className="text-xs font-black mb-2 inline-flex items-center gap-1"><ClipboardList size={12} /> 상태 변경 이력</p>
          {history.length === 0 ? (
            <p className="text-[11px] text-gray-500">기록 없음</p>
          ) : (
            <ul className="text-[11px] space-y-1" data-testid="seller-detail-history">
              {history.map((h) => (
                <li key={h.history_id} className="flex justify-between gap-2">
                  <span>
                    <span className="font-bold">{ACTION_LABELS[h.action] || h.action}</span>
                    {(h.action === 'status.update' || h.action === 'status.rollback') && (
                      <> · <span className="text-gray-500">{h.before?.status}</span> → <span className="font-bold">{h.after?.status}</span></>
                    )}
                    {h.action === 'invoice.set' && h.after?.shipment && (
                      <> · <span className="font-bold">{h.after.shipment.courier}</span> / <span className="font-mono">{h.after.shipment.tracking_number}</span></>
                    )}
                    <span className="text-gray-500"> · {h.actor_role}{h.actor_name ? ` (${h.actor_name})` : ''}</span>
                  </span>
                  <span className="text-gray-400 shrink-0">{format(new Date(h.created_at), 'MM.dd HH:mm', { locale: ko })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Date range presets (values written to URL as YYYY-MM-DD strings) -----
const toStr = (d) => {
  const z = new Date(d);
  z.setMinutes(z.getMinutes() - z.getTimezoneOffset());
  return z.toISOString().slice(0, 10);
};
const rangePresetFor = (kind) => {
  const now = new Date();
  if (kind === 'today')   return { from: toStr(now), to: toStr(now) };
  if (kind === 'yesterday') { const y = new Date(now); y.setDate(y.getDate() - 1); return { from: toStr(y), to: toStr(y) }; }
  if (kind === '7d')      { const p = new Date(now); p.setDate(now.getDate() - 6); return { from: toStr(p), to: toStr(now) }; }
  if (kind === '30d')     { const p = new Date(now); p.setDate(now.getDate() - 29); return { from: toStr(p), to: toStr(now) }; }
  if (kind === 'this_month') { const p = new Date(now.getFullYear(), now.getMonth(), 1); return { from: toStr(p), to: toStr(now) }; }
  if (kind === 'last_month') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toStr(start), to: toStr(end) };
  }
  return { from: '', to: '' };
};
const RANGE_PRESETS = [
  { key: 'all',        label: '전체' },
  { key: 'today',      label: '오늘' },
  { key: 'yesterday',  label: '어제' },
  { key: '7d',         label: '최근 7일' },
  { key: '30d',        label: '최근 30일' },
  { key: 'this_month', label: '이번 달' },
  { key: 'last_month', label: '지난 달' },
  { key: 'custom',     label: '직접 선택' },
];
const SORT_OPTIONS = [
  { key: 'recent',      label: '최신 주문순' },
  { key: 'oldest',      label: '오래된 주문순' },
  { key: 'amount_desc', label: '금액 높은순' },
  { key: 'amount_asc',  label: '금액 낮은순' },
];

// --- Main list section ---
const SellerOrdersSection = () => {
  // URL-persisted state so refreshing / reopening the detail modal / paging
  // never resets the seller's filter context.
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') || '';
  const q = sp.get('q') || '';
  const dateFrom = sp.get('from') || '';
  const dateTo = sp.get('to') || '';
  const rangeKind = sp.get('range') || (dateFrom || dateTo ? 'custom' : 'all');
  const sort = sp.get('sort') || 'recent';
  const page = Math.max(1, parseInt(sp.get('page') || '1', 10) || 1);
  const limit = [20, 50, 100].includes(parseInt(sp.get('limit'), 10))
    ? parseInt(sp.get('limit'), 10) : 20;

  const patch = (delta) => {
    const next = new URLSearchParams(sp);
    Object.entries(delta).forEach(([k, v]) => {
      if (v == null || v === '' || v === false) next.delete(k);
      else next.set(k, String(v));
    });
    setSp(next, { replace: true });
  };
  const setStatus = (v) => patch({ status: v, page: 1 });
  const setSort = (v) => patch({ sort: v, page: 1 });
  const setLimit = (v) => patch({ limit: v, page: 1 });
  const setPage = (v) => patch({ page: v });
  const setSearchQ = (v) => patch({ q: v, page: 1 });
  const applyRange = (kind) => {
    if (kind === 'custom') { patch({ range: 'custom', page: 1 }); return; }
    if (kind === 'all')    { patch({ range: '', from: '', to: '', page: 1 }); return; }
    const r = rangePresetFor(kind);
    patch({ range: kind, from: r.from, to: r.to, page: 1 });
  };
  const setCustomFrom = (v) => patch({ from: v, range: 'custom', page: 1 });
  const setCustomTo   = (v) => patch({ to: v,   range: 'custom', page: 1 });

  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [invoiceMode, setInvoiceMode] = useState(false);
  const [invoiceRows, setInvoiceRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [rollbackRow, setRollbackRow] = useState(null);
  const [bulkRollbackFrom, setBulkRollbackFrom] = useState(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [combinedOpen, setCombinedOpen] = useState(false);
  const [combinedCandidateCount, setCombinedCandidateCount] = useState(0);
  useEffect(() => {
    (async () => {
      try { const r = await (await import('@/lib/api')).default.get('/seller/combined-shipments/candidates');
        setCombinedCandidateCount((r.data.items || []).length);
      } catch { /* silent · optional hint */ }
    })();
  }, [total]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportBtnRef = useRef(null);
  useEffect(() => {
    if (!exportOpen) return;
    const onDoc = (e) => { if (!exportBtnRef.current?.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [exportOpen]);
  const currentFilters = { status, dateFrom, dateTo, q, sort };
  const runExport = async ({ format, template, useSelection = false }) => {
    setExportOpen(false);
    const ids = useSelection ? Array.from(selected) : null;
    await downloadOrders({ format, template, filters: currentFilters, ids });
  };
  useEffect(() => setSearchDraft(q), [q]);
  const runSearch = () => setSearchQ(searchDraft.trim());
  const clearSearch = () => { setSearchDraft(''); setSearchQ(''); };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort };
      if (status)   params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      if (q)        params.q         = q;
      const summaryParams = {};
      if (dateFrom) summaryParams.date_from = dateFrom;
      if (dateTo)   summaryParams.date_to   = dateTo;
      if (q)        summaryParams.q         = q;
      const [listRes, sumRes] = await Promise.all([
        api.get('/seller/orders', { params }),
        api.get('/seller/orders/summary', { params: summaryParams }),
      ]);
      const env = listRes.data || {};
      setOrders(env.items || []);
      setTotal(env.total ?? (env.items || []).length);
      setPageCount(env.page_count ?? 1);
      setSummary(sumRes.data?.by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || '주문 목록 로드 실패');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); setSelected(new Set()); setInvoiceMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dateFrom, dateTo, q, sort, page, limit]);

  const filtered = orders;   // Server already applied every filter.

  const allChecked = filtered.length > 0 && filtered.every((o) => selected.has(o.seller_order_id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(filtered.map((o) => o.seller_order_id)));
  };
  const toggleOne = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const bulkChange = async (target) => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('주문을 선택해주세요.'); return; }
    const allowedFrom = {
      paid:      ['payment_pending'],
      preparing: ['paid'],
      shipping:  ['paid', 'preparing'],
      delivered: ['shipping'],
    }[target] || [];
    const actionable = filtered.filter((o) => selected.has(o.seller_order_id) && allowedFrom.includes(o.status));
    const targetLabel = STATUS_TABS.find((t) => t.key === target)?.label || target;
    const msg = `선택한 ${ids.length}건 중 ${actionable.length}건만 ${targetLabel} 처리가 가능합니다. 진행하시겠습니까?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const res = await api.post('/seller/orders/bulk-status', {
        seller_order_ids: ids, target_status: target,
      });
      toast.success(`${(res.data?.success_ids || []).length}건 처리됨${(res.data?.skipped || []).length ? ` · ${res.data.skipped.length}건 스킵` : ''}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '일괄 처리 실패'); }
    finally { setBusy(false); }
  };

  // Row-level status change (used from card buttons).
  const rowChangeStatus = async (sid, target) => {
    setBusy(true);
    try {
      await api.patch(`/seller/orders/${sid}/status`, { status: target });
      const label = STATUS_TABS.find((t) => t.key === target)?.label || target;
      toast.success(`${label}(으)로 변경되었습니다.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '상태 변경 실패'); }
    finally { setBusy(false); }
  };

  const rowDelete = async (sid, curStatus) => {
    if (!DELETABLE_STATUSES.has(curStatus)) {
      toast.error('배송준비 이후 주문은 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm('정말 삭제하시겠습니까? 삭제된 주문은 목록에서 보이지 않게 됩니다.')) return;
    setBusy(true);
    try {
      await api.delete(`/seller/orders/${sid}`);
      toast.success('주문이 삭제되었습니다.');
      setSelected((s) => { const n = new Set(s); n.delete(sid); return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '삭제 실패'); }
    finally { setBusy(false); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('주문을 선택해주세요.'); return; }
    const selectedRows = filtered.filter((o) => selected.has(o.seller_order_id));
    const deletable = selectedRows.filter((o) => DELETABLE_STATUSES.has(o.status));
    if (deletable.length === 0) {
      toast.error('배송준비 이후 주문은 삭제할 수 없습니다.');
      return;
    }
    if (deletable.length < ids.length) {
      if (!window.confirm(`선택한 ${ids.length}건 중 삭제 가능한 ${deletable.length}건만 삭제됩니다. 진행하시겠습니까?`)) return;
    } else {
      if (!window.confirm(`선택한 ${ids.length}건을 삭제하시겠습니까?`)) return;
    }
    setBusy(true);
    try {
      const res = await api.post('/seller/orders/bulk-delete', {
        seller_order_ids: deletable.map((o) => o.seller_order_id),
      });
      const s = res.data || {};
      toast.success(`${(s.success_ids || []).length}건 삭제됨${(s.skipped || []).length ? ` · ${s.skipped.length}건 스킵` : ''}`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '일괄 삭제 실패'); }
    finally { setBusy(false); }
  };

  const startBulkInvoice = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('주문을 선택해주세요.'); return; }
    const initial = {};
    filtered.forEach((o) => {
      if (selected.has(o.seller_order_id)) {
        initial[o.seller_order_id] = {
          courier: o.shipment?.courier || 'CJ대한통운',
          tracking_number: o.shipment?.tracking_number || '',
        };
      }
    });
    setInvoiceRows(initial);
    setInvoiceMode(true);
  };
  const saveBulkInvoice = async () => {
    const items = Object.entries(invoiceRows)
      .filter(([_, v]) => (v.tracking_number || '').trim())
      .map(([sid, v]) => ({ seller_order_id: sid, courier: v.courier, tracking_number: v.tracking_number.trim() }));
    if (items.length === 0) { toast.error('저장할 송장 정보가 없습니다.'); return; }
    if (!window.confirm(`${items.length}건의 송장을 저장하고 배송중으로 변경합니다.`)) return;
    setBusy(true);
    try {
      const res = await api.post('/seller/orders/bulk-invoice', { items });
      const s = res.data || {};
      toast.success(`${(s.success_ids || []).length}건 저장${(s.skipped || []).length ? ` · ${s.skipped.length}건 스킵` : ''}${(s.warnings || []).length ? ` · ${s.warnings.length}건 경고` : ''}`);
      if ((s.warnings || []).length) console.warn('bulk-invoice warnings', s.warnings);
      setInvoiceMode(false); setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '일괄 송장 저장 실패'); }
    finally { setBusy(false); }
  };

  // Rollback per-row from the card. Only status ∈ paid|preparing|shipping|delivered.
  const doRowRollback = async ({ reason, keep_shipment }) => {
    if (!rollbackRow) return;
    setBusy(true);
    try {
      await api.post(`/seller/orders/${rollbackRow.seller_order_id}/rollback`, { reason, keep_shipment });
      toast.success('이전 단계로 되돌렸습니다.');
      setRollbackRow(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '되돌리기 실패'); }
    finally { setBusy(false); }
  };
  // Bulk rollback. `bulkRollbackFrom` is the current tab's status; we send it
  // as `from_status` so the API only rolls back orders that actually match.
  const doBulkRollback = async ({ reason, keep_shipment }) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const res = await api.post('/seller/orders/bulk-rollback', {
        seller_order_ids: ids, reason, keep_shipment, from_status: bulkRollbackFrom,
      });
      const s = res.data || {};
      toast.success(`${(s.success_ids || []).length}건 되돌림${(s.skipped || []).length ? ` · ${s.skipped.length}건 스킵` : ''}`);
      setBulkRollbackFrom(null); setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '일괄 되돌리기 실패'); }
    finally { setBusy(false); }
  };
  const openBulkRollback = () => {
    if (!ROLLBACK_MAP[status]) { toast.error('현재 탭에서는 일괄 되돌리기가 불가합니다.'); return; }
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('주문을 선택해주세요.'); return; }
    // Preview: how many are actually eligible (same status as tab)?
    const actionable = filtered.filter((o) => selected.has(o.seller_order_id) && o.status === status);
    if (actionable.length < ids.length) {
      if (!window.confirm(`선택한 ${ids.length}건 중 ${actionable.length}건만 이전 단계 처리가 가능합니다. 진행하시겠습니까?`)) return;
    }
    setBulkRollbackFrom(status);
  };

  return (
    <div className="space-y-4" data-testid="seller-orders-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>주문관리</h2>
        <div className="flex items-center gap-1 flex-wrap">
          <button type="button" onClick={() => setBulkUploadOpen(true)}
            className="px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-bold inline-flex items-center gap-1"
            data-testid="seller-orders-bulk-upload-btn">
            <FileSpreadsheet size={12} /> 송장 대량등록
          </button>
          <div className="relative inline-block" ref={exportBtnRef}>
            <button type="button" onClick={() => setExportOpen((v) => !v)}
              className="px-3 py-1.5 rounded-full bg-black text-white text-xs font-bold inline-flex items-center gap-1"
              data-testid="seller-orders-export-btn">
              <Download size={12} /> 주문 다운로드 <ChevronDown size={11} />
            </button>
            {exportOpen && (
              <div className="absolute right-0 mt-1 z-30 w-64 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden text-xs"
                   data-testid="seller-orders-export-menu">
                <p className="px-3 pt-2 pb-1 text-[10px] font-black text-gray-400 uppercase">현재 조건으로</p>
                <button onClick={() => runExport({ format: 'xlsx' })}
                        className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/5 font-bold"
                        data-testid="export-xlsx-filter">Excel(.xlsx) 다운로드</button>
                <button onClick={() => runExport({ format: 'csv' })}
                        className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/5 font-bold"
                        data-testid="export-csv-filter">CSV 다운로드</button>
                <button onClick={() => runExport({ format: 'xlsx', template: 'invoice' })}
                        className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/5 font-bold text-[#FF3B30]"
                        data-testid="export-invoice-template">송장 등록용 주문파일</button>
                {selected.size > 0 && (
                  <>
                    <div className="border-t border-black/10 dark:border-white/10" />
                    <p className="px-3 pt-2 pb-1 text-[10px] font-black text-gray-400 uppercase">선택 {selected.size}건</p>
                    <button onClick={() => runExport({ format: 'xlsx', useSelection: true })}
                            className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/5 font-bold"
                            data-testid="export-xlsx-selected">선택 주문 Excel 다운로드</button>
                    <button onClick={() => runExport({ format: 'xlsx', template: 'invoice', useSelection: true })}
                            className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/5 font-bold text-[#FF3B30]"
                            data-testid="export-invoice-template-selected">선택 주문 송장 등록용 파일</button>
                  </>
                )}
              </div>
            )}
          </div>
          <button type="button" onClick={load} className="px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold inline-flex items-center gap-1" data-testid="seller-orders-refresh">
            <RefreshCw size={12} /> 새로고침
          </button>
        </div>
      </div>

      <div className="flex gap-2 items-center" data-testid="seller-orders-search-bar">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            placeholder="주문번호 · 주문자명 · 휴대폰번호 · 이메일 · 상품명 · 송장번호"
            className="w-full pl-8 pr-3 py-2 text-xs rounded-full border border-black/10 dark:border-white/10 bg-transparent"
            data-testid="seller-orders-search-input" />
        </div>
        <button onClick={runSearch}
          className="px-3 py-2 text-xs font-bold rounded-full bg-black text-white"
          data-testid="seller-orders-search-btn">검색</button>
        {q && (
          <button onClick={clearSearch} className="px-3 py-2 text-xs rounded-full border border-black/10 dark:border-white/10" data-testid="seller-orders-search-clear">해제</button>
        )}
        <button onClick={() => setMobileFilterOpen((v) => !v)}
          className="md:hidden px-3 py-2 text-xs font-bold rounded-full border border-black/10 dark:border-white/10 inline-flex items-center gap-1"
          data-testid="seller-orders-mobile-filter-toggle">
          <Filter size={11} /> 필터
        </button>
      </div>

      <div className={`space-y-2 ${mobileFilterOpen ? '' : 'hidden md:block'}`} data-testid="seller-orders-filter-panel">
        <div className="flex gap-1 flex-wrap" data-testid="seller-orders-range-tabs">
          {RANGE_PRESETS.map((p) => (
            <button key={p.key} onClick={() => applyRange(p.key)}
              className={`px-3 py-1 rounded-full text-[11px] font-bold border ${(rangeKind || 'all') === p.key ? 'bg-black text-white border-black dark:bg-white dark:text-black' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}
              data-testid={`seller-orders-range-${p.key}`}>{p.label}</button>
          ))}
        </div>
        {rangeKind === 'custom' && (
          <div className="flex items-center gap-2 text-xs" data-testid="seller-orders-custom-range">
            <input type="date" value={dateFrom} onChange={(e) => setCustomFrom(e.target.value)}
              className="px-2 py-1 border rounded bg-transparent border-black/10 dark:border-white/10"
              data-testid="seller-orders-from" />
            <span className="text-gray-400">~</span>
            <input type="date" value={dateTo} onChange={(e) => setCustomTo(e.target.value)}
              className="px-2 py-1 border rounded bg-transparent border-black/10 dark:border-white/10"
              data-testid="seller-orders-to" />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="inline-flex items-center gap-1 text-gray-500">
            정렬
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="px-2 py-1 border rounded bg-transparent border-black/10 dark:border-white/10 text-xs"
              data-testid="seller-orders-sort">
              {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1 text-gray-500">
            페이지당
            <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}
              className="px-2 py-1 border rounded bg-transparent border-black/10 dark:border-white/10 text-xs"
              data-testid="seller-orders-limit">
              <option value={20}>20건</option>
              <option value={50}>50건</option>
              <option value={100}>100건</option>
            </select>
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="seller-orders-tabs">
        {STATUS_TABS.map((t) => (
          <button key={t.key || 'all'} type="button" onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${status === t.key ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}
            data-testid={`seller-orders-tab-${t.key || 'all'}`}>
            {t.label}
            {t.key && summary[t.key] != null && <span className="ml-1 text-[10px] opacity-70">({summary[t.key]})</span>}
          </button>
        ))}
      </div>

      {/* Bulk action bar — status-aware */}
      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-black/[0.03] dark:bg-white/[0.03]" data-testid="seller-orders-bulk-bar">
          <label className="inline-flex items-center gap-1 text-xs font-bold">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="seller-orders-select-all" />
            전체선택
          </label>
          <span className="text-xs text-gray-500" data-testid="seller-orders-selected-count">{selected.size}건 선택됨</span>
          <div className="flex flex-wrap gap-1 ml-auto">
            {/* Forward transitions per current tab */}
            {status === 'payment_pending' && !invoiceMode && (
              <button onClick={() => bulkChange('paid')} disabled={busy || selected.size === 0}
                className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white disabled:opacity-40"
                data-testid="seller-orders-bulk-paid">선택 주문 결제완료</button>
            )}
            {status === 'paid' && !invoiceMode && (
              <button onClick={() => bulkChange('preparing')} disabled={busy || selected.size === 0}
                className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white disabled:opacity-40"
                data-testid="seller-orders-bulk-preparing">선택 주문 배송준비</button>
            )}
            {status === 'preparing' && !invoiceMode && (
              <>
                <button onClick={() => bulkChange('shipping')} disabled={busy || selected.size === 0}
                  className="px-3 py-1.5 rounded-full text-xs font-bold border border-violet-500/40 text-violet-600 disabled:opacity-40"
                  data-testid="seller-orders-bulk-shipping">선택 주문 배송중</button>
                <button onClick={startBulkInvoice} disabled={busy || selected.size === 0}
                  className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white disabled:opacity-40 inline-flex items-center gap-1"
                  data-testid="seller-orders-bulk-invoice-start"><Truck size={12} /> 선택 주문 송장입력</button>
                {combinedCandidateCount > 0 && (
                  <button onClick={() => setCombinedOpen(true)} disabled={busy}
                    className="px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500 text-white disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid="seller-orders-combined-open">📦 합포 가능 {combinedCandidateCount}건</button>
                )}
              </>
            )}
            {status === 'shipping' && !invoiceMode && (
              <button onClick={() => bulkChange('delivered')} disabled={busy || selected.size === 0}
                className="px-3 py-1.5 rounded-full text-xs font-bold border border-emerald-500/30 text-emerald-600 disabled:opacity-40"
                data-testid="seller-orders-bulk-delivered">선택 주문 배송완료</button>
            )}

            {/* Invoice mode save/cancel */}
            {invoiceMode && (
              <>
                <button onClick={saveBulkInvoice} disabled={busy} className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white disabled:opacity-40" data-testid="seller-orders-bulk-invoice-save">선택 송장 저장</button>
                <button onClick={() => setInvoiceMode(false)} className="px-3 py-1.5 rounded-full text-xs font-bold border border-black/10 dark:border-white/10">송장 입력 종료</button>
              </>
            )}

            {/* Rollback (only if current tab status has a previous step) */}
            {ROLLBACK_MAP[status] && !invoiceMode && (
              <button onClick={openBulkRollback} disabled={busy || selected.size === 0}
                className="px-3 py-1.5 rounded-full text-xs font-bold border border-amber-500/40 text-amber-700 dark:text-amber-400 disabled:opacity-40"
                data-testid="seller-orders-bulk-rollback">↶ 선택 주문 {ROLLBACK_MAP[status].label}</button>
            )}

            {/* Delete — only available when the current tab (or 전체) can contain deletable rows */}
            {(status === '' || status === 'payment_pending' || status === 'paid') && !invoiceMode && (
              <button onClick={bulkDelete} disabled={busy || selected.size === 0}
                className="px-3 py-1.5 rounded-full text-xs font-bold border border-red-500/40 text-red-600 disabled:opacity-40 inline-flex items-center gap-1"
                data-testid="seller-orders-bulk-delete"><Trash2 size={12} /> 선택 주문 삭제</button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-400 py-6">불러오는 중…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="seller-orders-empty">
          <Package className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">해당 상태의 주문이 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="seller-orders-list">
          {filtered.map((so) => (
            <div key={so.seller_order_id} className="rounded-2xl border border-black/10 dark:border-white/10 p-4" data-testid={`seller-order-${so.seller_order_id}`}>
              <div className="flex items-start gap-3 mb-2">
                <input type="checkbox"
                  checked={selected.has(so.seller_order_id)}
                  onChange={() => toggleOne(so.seller_order_id)}
                  className="mt-1"
                  data-testid={`seller-order-check-${so.seller_order_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500 min-w-0">
                      <span className="font-bold">주문번호 {so.order_number}</span>
                      <span className="mx-1.5">·</span>
                      {so.created_at && format(new Date(so.created_at), 'yyyy.MM.dd HH:mm', { locale: ko })}
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLE[so.status] || 'bg-gray-100'} shrink-0`} data-testid={`seller-order-status-${so.seller_order_id}`}>{so.status_label}</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    <div>
                      <p className="text-[11px] text-gray-500 mb-0.5">구매자</p>
                      <p className="text-sm font-bold">{so.buyer_snapshot?.name}</p>
                      <p className="text-[11px] text-gray-500">{so.shipping_address_snapshot?.recipient_name} · {so.shipping_address_snapshot?.phone}</p>
                      <p className="text-[11px] text-gray-500 truncate">[{so.shipping_address_snapshot?.postal_code}] {so.shipping_address_snapshot?.address1} {so.shipping_address_snapshot?.address2}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500 mb-0.5">상품 ({so.items?.length}건) · 합계 {won(so.totals?.total)}</p>
                      <ul className="space-y-1">
                        {(so.items || []).slice(0, 2).map((it, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
                              <SafeImg src={it.image_snapshot} kind="product" alt="" className="w-full h-full object-cover" />
                            </div>
                            <span className="text-xs truncate flex-1">{it.name_snapshot} × {it.quantity}</span>
                          </li>
                        ))}
                        {(so.items || []).length > 2 && <li className="text-[11px] text-gray-400">외 {so.items.length - 2}건</li>}
                      </ul>
                    </div>
                  </div>

                  {so.shipment?.tracking_number && !invoiceMode && (() => {
                    const sh = so.shipment || {};
                    const status = getInternalShipmentLabel({ shipment: sh, orderStatus: so.status });
                    const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                    const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                    return (
                      <div className="mt-2 p-2 rounded-lg bg-black/[0.03] dark:bg-white/[0.03] text-[11px] flex items-center gap-2 flex-wrap" data-testid={`seller-order-shipment-${so.seller_order_id}`}>
                        {so.combined_shipment_id && (
                          <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold text-[10px]" data-testid={`seller-order-combined-${so.seller_order_id}`}>📦 합포배송</span>
                        )}
                        <span><span className="font-bold">배송:</span> {sh.courier} · <span className="font-mono">{sh.tracking_number}</span></span>
                        {status && <span className="px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold text-[10px]" data-testid={`seller-order-track-status-${so.seller_order_id}`}>{status}</span>}
                        {available && url ? (
                          <a href={url} target="_blank" rel="noreferrer noopener"
                             className="ml-auto text-[11px] font-bold text-[#FF3B30] underline underline-offset-2 hover:text-[#e0332a]"
                             data-testid={`seller-order-track-${so.seller_order_id}`}>
                            배송조회 ↗
                          </a>
                        ) : (
                          <span className="ml-auto text-[10px] text-gray-400" data-testid={`seller-order-track-pending-${so.seller_order_id}`}>배송조회 준비중</span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Inline invoice-input mode for THIS row when selected */}
                  {invoiceMode && selected.has(so.seller_order_id) && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`seller-order-inv-row-${so.seller_order_id}`}>
                      <select value={invoiceRows[so.seller_order_id]?.courier || 'CJ대한통운'}
                        onChange={(e) => setInvoiceRows((r) => ({ ...r, [so.seller_order_id]: { ...(r[so.seller_order_id] || {}), courier: e.target.value } }))}
                        className="px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent">
                        {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input value={invoiceRows[so.seller_order_id]?.tracking_number || ''}
                        onChange={(e) => setInvoiceRows((r) => ({ ...r, [so.seller_order_id]: { ...(r[so.seller_order_id] || {}), tracking_number: e.target.value } }))}
                        placeholder="송장번호" className="flex-1 min-w-[140px] px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent"
                        data-testid={`seller-order-inv-tracking-${so.seller_order_id}`} />
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => setOpenId(so.seller_order_id)} className="px-3 py-1.5 rounded-full text-xs font-bold border border-black/10 dark:border-white/10" data-testid={`seller-order-detail-${so.seller_order_id}`}>상세보기</button>

                    {so.status === 'payment_pending' && !invoiceMode && (
                      <button onClick={() => rowChangeStatus(so.seller_order_id, 'paid')} disabled={busy}
                        className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white"
                        data-testid={`seller-order-paid-${so.seller_order_id}`}>결제완료 처리</button>
                    )}
                    {so.status === 'paid' && !invoiceMode && (
                      <button onClick={() => rowChangeStatus(so.seller_order_id, 'preparing')} disabled={busy}
                        className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white"
                        data-testid={`seller-order-preparing-${so.seller_order_id}`}>배송준비 처리</button>
                    )}
                    {so.status === 'preparing' && !invoiceMode && (
                      <>
                        <button onClick={() => setOpenId(so.seller_order_id)}
                          className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white"
                          data-testid={`seller-order-invoice-${so.seller_order_id}`}>{so.shipment?.tracking_number ? '송장 수정' : '송장입력'}</button>
                        <button onClick={() => rowChangeStatus(so.seller_order_id, 'shipping')}
                          disabled={busy || !so.shipment?.tracking_number}
                          title={!so.shipment?.tracking_number ? '송장 입력 후 사용하세요.' : ''}
                          className="px-3 py-1.5 rounded-full text-xs font-bold border border-violet-500/40 text-violet-600 disabled:opacity-40"
                          data-testid={`seller-order-shipping-${so.seller_order_id}`}>배송중 처리</button>
                      </>
                    )}
                    {so.status === 'shipping' && !invoiceMode && (
                      <>
                        <button onClick={() => setOpenId(so.seller_order_id)}
                          className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white"
                          data-testid={`seller-order-invoice-${so.seller_order_id}`}>송장 수정</button>
                        <button onClick={() => rowChangeStatus(so.seller_order_id, 'delivered')} disabled={busy}
                          className="px-3 py-1.5 rounded-full text-xs font-bold border border-emerald-500/40 text-emerald-600"
                          data-testid={`seller-order-delivered-${so.seller_order_id}`}>배송완료 처리</button>
                      </>
                    )}

                    {ROLLBACK_MAP[so.status] && !invoiceMode && (
                      <button onClick={() => setRollbackRow({ seller_order_id: so.seller_order_id, status: so.status })}
                        disabled={busy}
                        className="px-3 py-1.5 rounded-full text-xs font-bold border border-amber-500/40 text-amber-700 dark:text-amber-400"
                        data-testid={`seller-order-rollback-${so.seller_order_id}`}>↶ {ROLLBACK_MAP[so.status].label}</button>
                    )}
                    {DELETABLE_STATUSES.has(so.status) && !invoiceMode && (
                      <button onClick={() => rowDelete(so.seller_order_id, so.status)} disabled={busy}
                        className="px-3 py-1.5 rounded-full text-xs font-bold border border-red-500/40 text-red-600 inline-flex items-center gap-1 ml-auto"
                        data-testid={`seller-order-delete-${so.seller_order_id}`}><Trash2 size={12} /> 삭제</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {openId && (
        <SellerOrderDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />
      )}

      {/* Pagination + summary bar */}
      {!loading && total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-2" data-testid="seller-orders-pagination">
          <p className="text-[11px] text-gray-500" data-testid="seller-orders-range-info">
            전체 <b>{total.toLocaleString('ko-KR')}</b>건 · {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)}건 표시
          </p>
          <div className="inline-flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
              className="px-2 py-1 text-xs font-bold border border-black/10 dark:border-white/10 rounded-full disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="seller-orders-page-prev"><ChevronLeft size={12} /> 이전</button>
            {(() => {
              // Windowed page-number strip · at most 5 buttons.
              const total_p = pageCount;
              const start = Math.max(1, Math.min(page - 2, Math.max(1, total_p - 4)));
              const end = Math.min(total_p, start + 4);
              const arr = [];
              for (let i = start; i <= end; i += 1) arr.push(i);
              return arr.map((n) => (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-8 h-7 text-xs font-bold rounded-full ${n === page ? 'bg-[#FF3B30] text-white' : 'border border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}
                  data-testid={`seller-orders-page-${n}`}>{n}</button>
              ));
            })()}
            <button onClick={() => setPage(Math.min(pageCount, page + 1))} disabled={page >= pageCount}
              className="px-2 py-1 text-xs font-bold border border-black/10 dark:border-white/10 rounded-full disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="seller-orders-page-next">다음 <ChevronRight size={12} /></button>
          </div>
        </div>
      )}

      <SellerCombinedShipmentModal
        open={combinedOpen}
        onClose={() => setCombinedOpen(false)}
        onChanged={() => { load(); (async () => { try { const r = await (await import('@/lib/api')).default.get('/seller/combined-shipments/candidates'); setCombinedCandidateCount((r.data.items || []).length); } catch {} })(); }}
      />
      <BulkInvoiceUploadModal open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)}
        onDone={load}
        onOpenInvoiceTemplate={() => runExport({ format: 'xlsx', template: 'invoice' })} />

      <RollbackConfirmModal
        open={!!rollbackRow} from={rollbackRow?.status} busy={busy}
        onCancel={() => setRollbackRow(null)} onConfirm={doRowRollback}
      />
      <RollbackConfirmModal
        open={!!bulkRollbackFrom} from={bulkRollbackFrom} count={selected.size} busy={busy}
        onCancel={() => setBulkRollbackFrom(null)} onConfirm={doBulkRollback}
      />
    </div>
  );
};

export default SellerOrdersSection;
